export enum E_ITEM_TYPE {
  BOUNDED_TEXT = 'BoundedTextItem',
  PLAIN_TEXT = 'PlainTextItem',
  PLACEHOLDER = 'PlaceholderItem',
  LAYOUT = 'LayoutItem',
};

export type TColorSpace = 0 | 1; // 0 = RGB, 1 = CMYK

export interface IColor {
  r?: number;
  g?: number;
  b?: number;
  c?: number;
  m?: number;
  y?: number;
  k?: number;
  alpha: number;
  profile: string | null;
  colorSpace: TColorSpace;
}

export interface ITransform {
  angle: number;
  scaleX: number;
  scaleY: number;
  translateX: number;
  translateY: number;
  isEmpty: boolean;
  hasTranslate: boolean;
  hasScale: boolean;
}

export interface IRectangle {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface IPath {
  path: string;
}

export interface IPermissions {
  [key: string]: any;
}

// ========== Text Item Types ==========
export interface IFont {
  postScriptName: string;
  family: string | null;
  style: string | null;
  size: number;
  fauxBold: boolean;
  fauxItalic: boolean;
  allCaps: boolean;
  openTypeFeatures: { tag: string; value: number }[];
}

export interface ITextViolationSettings {
  allowTextCrop: boolean;
  allowInPlaceText: boolean;
  allowShape: boolean;
  allowSafetyLines: boolean;
  allowBleedArea: boolean;
  allowRegion: boolean;
  type: string;
}

export interface ITextPermissions {
  allowChangeText: boolean;
  allowChangeFont: boolean;
  allowChangeFontSize: boolean;
  allowChangeFontColor: boolean;
  allowChangeTextAlignment: boolean;
  allowChangeTextBIU: boolean;
  allowChangeShadow: boolean;
  allowChangeStroke: boolean;
  allowTextFormatting: null;
  allowChangeStrokeColor: boolean;
  allowChangeLeading: boolean;
}

export interface ITextItem {
  $type: E_ITEM_TYPE.BOUNDED_TEXT | E_ITEM_TYPE.PLAIN_TEXT;
  id: string;
  name: string;
  version: string;

  text: string;
  originalText: string;
  font: IFont;
  color: IColor;
  alignment: number;
  textRectangle: IRectangle;
  sourceRectangle: IRectangle;
  sourcePath: IPath;

  characterLimit: number;
  maxLineCount: number | null;
  horizontalScale: number;
  verticalScale: number;

  textPermissions: ITextPermissions;
  violationSettings: ITextViolationSettings;

  isVariable: boolean;
  locked: boolean;
  visible: boolean;
  opacity: number;
  transform: ITransform;
  blendMode: string;
  textWrappingMode: number;

  // Shape-related
  borderWidth: number;
  borderColor: IColor;
  fillColor: IColor;
  shapePermissions: {
    allowChangeBorderWidth: boolean;
    allowChangeFillColor: boolean;
    allowChangeBorderColor: boolean;
  };

  // Permissions
  frontendPermissions: { displayInObjectInspector: boolean };
  manipulationPermissions: {
    allowDelete: boolean;
    allowMoveHorizontal: boolean;
    allowMoveVertical: boolean;
    allowRotate: boolean;
    allowDragAndDrop: boolean;
    resizeGrips: {
      edge: boolean;
      corner: ("Arbitrary" | "Proportional")[];
    };
  };
  visualizationPermissions: { noPrint: boolean; noShow: boolean };
  itemPermissions: {
    allowZOrderChange: boolean;
    allowOpacityChange: boolean;
    allowRemoveOnLayoutChange: boolean;
    itemToolbarPermissions: {
      showDeleteButton: boolean;
      showEditButton: boolean;
      showSelectButton: boolean;
      showEditButtonConstraint: boolean | null;
      showSelectButtonConstraint: boolean | null;
      showHandleButton: boolean;
    };
  };

  // Misc
  tags: Record<string, any>;
  themeBinding: Record<string, any>;
  listStyles: string;
  stroke: any;
  shadow: any;
  overprintText: boolean;
  overprintStroke: boolean;
  overprintFill: boolean;
  mask: any;
  class: string | null;
  parentContainer: any;
  paragraphSettings: Record<string, number>;
  verticalAlignment: number;
  isVertical: boolean;
  includeBorder: boolean;
  colorPalette: IColor[];
}

// ========== Placeholder Item Types ==========
export interface IImagePermissions {
  allowChangeImage: boolean;
  allowEditImage: boolean;
  allowEditImageConstraint: any;
  allowKeepOverlayColor: boolean;
}

export interface IImageViolationSettings {
  allowImageQuality: boolean;
  allowShape: boolean;
  allowSafetyLines: boolean;
  allowBleedArea: boolean;
  allowRegion: boolean;
  type: "ImageViolationSettings";
}

export interface IPlaceholderItem {
  $type: E_ITEM_TYPE.PLACEHOLDER;
  id: string;
  name: string;
  version: string;

  content: any;
  contentResizeMode: number;
  contentHorizontalAlignment: number;
  contentVerticalAlignment: number;

  allowedSubfolder: string;
  isStubContent: boolean;
  fixedStubContentSize: boolean;
  isCoverMode: boolean;

  sourceRectangle: IRectangle;
  sourcePath: IPath;

  contentPermissions: {
    imagePermissions: IImagePermissions;
    barcodePermissions: Record<string, boolean>;
  };
  violationSettings: IImageViolationSettings;
  placeholderPermissions: {
    allowEditContent: boolean;
    showSelectButton: boolean;
    showDoneButton: boolean;
    showHandleButton: boolean;
    showSelectButtonConstraint: any;
  };

  transform: ITransform;
  blendMode: string;
  textWrappingMode: number;

  locked: boolean;
  visible: boolean;
  opacity: number;
  isVariable: boolean;

  // Shape
  borderWidth: number;
  borderColor: IColor;
  fillColor: IColor;
  shapePermissions: {
    allowChangeBorderWidth: boolean;
    allowChangeFillColor: boolean;
    allowChangeBorderColor: boolean;
  };

  // Permissions
  frontendPermissions: { displayInObjectInspector: boolean };
  manipulationPermissions: {
    allowDelete: boolean;
    allowMoveHorizontal: boolean;
    allowMoveVertical: boolean;
    allowRotate: boolean;
    allowDragAndDrop: boolean;
    resizeGrips: {
      edge: boolean;
      corner: ("Proportional")[];
    };
  };
  visualizationPermissions: { noPrint: boolean; noShow: boolean };
  itemPermissions: {
    allowZOrderChange: boolean;
    allowOpacityChange: boolean;
    allowRemoveOnLayoutChange: boolean;
    itemToolbarPermissions: {
      showDeleteButton: boolean;
      showEditButton: boolean;
      showSelectButton: boolean;
      showEditButtonConstraint: boolean;
      showSelectButtonConstraint: boolean;
      showHandleButton: boolean;
    };
  };

  tags: Record<string, any>;
  themeBinding: Record<string, any>;
  mask: any;
  class: string | null;
}

// ========== Layout / Container Types ==========
export type TItem = ITextItem | IPlaceholderItem | ILayoutItem;

export interface ILayoutItem {
  $type: E_ITEM_TYPE.LAYOUT;
  id: string;
  name: string;
  version: string;

  items: TItem[];
  sourceRectangle: IRectangle;

  layoutSettings: {
    margin: number;
    alignItems: number;
    orientation: number;
    anchorPoint: number;
    type: "AutoLayoutSettings";
  };

  tags: {
    $layoutItemsOrder: string[];
  };

  transform: ITransform;
  blendMode: string;
  textWrappingMode: number;
  locked: boolean;
  visible: boolean;
  opacity: number;
  isVariable: boolean;

  groupItemPermissions: {
    allowReplaceContent: boolean;
    allowUngroup: boolean;
    allowSelectNestedItems: boolean;
  };

  frontendPermissions: { displayInObjectInspector: boolean };
  manipulationPermissions: {
    allowDelete: boolean;
    allowMoveHorizontal: boolean;
    allowMoveVertical: boolean;
    allowRotate: boolean;
    allowDragAndDrop: boolean;
    resizeGrips: {
      edge: boolean;
      corner: ("Arbitrary" | "Proportional")[];
    };
  };
  visualizationPermissions: { noPrint: boolean; noShow: boolean };
  itemPermissions: {
    allowZOrderChange: boolean;
    allowOpacityChange: boolean;
    allowRemoveOnLayoutChange: boolean;
    itemToolbarPermissions: {
      showDeleteButton: boolean;
      showEditButton: boolean;
      showSelectButton: boolean;
      showEditButtonConstraint: boolean;
      showSelectButtonConstraint: boolean;
      showHandleButton: boolean;
    };
  };
  violationSettings: {
    allowSafetyLines: boolean;
    allowBleedArea: boolean;
    allowRegion: boolean;
    type: "ViolationSettings";
  };

  themeBinding: Record<string, any>;
  mask: any;
  class: string | null;
}

export interface IContainer {
  id: string;
  name: string;
  version: string;
  type: "ColorLessContainer";
  items: TItem[];
  renderingType: number;
  opaque: boolean;
  format: number;
  dither: number;
  amount: number;
  dpi: number | null;
  locked: boolean;
  visible: boolean;

  outputColor: IColor;
  visualization: {
    color: IColor;
    opacity: number;
    enableGlareEffect: boolean;
    type: "ColorVisualization";
  };

  // Permissions omitted for brevity but can be added if needed
}

// ========== Print Area ==========
export interface IPrintArea {
  id: string;
  name: string;
  version: string;
  bounds: IRectangle;
  bleed: IRectangle;
  slug: IRectangle;
  safetyLines: any[];
  cropMarks: any[];
  tags: Record<string, any>;
}

// ========== Surface ==========
export interface ISurface {
  id: string;
  name: string;
  version: string;
  width: number;
  height: number;
  rotateAngle: number;
  size: string; // e.g. "267.552, 391.392"
  tags: Record<string, any>;
  mockup: {
    id: string;
    name: string;
    version: string;
    tags: Record<string, any>;
    underContainers: any[];
    overContainers: any[];
  };
  previewMockups: any[];
  printAreas: IPrintArea[];
  containers: IContainer[];
  locked: boolean;
  visible: boolean;
  printingTechniqueConstraints: {
    allowRasterElements: boolean;
    minWidth: number;
    minFontSize: number;
    targetDpi: number;
  };
}

// ========== Product Settings ==========
export interface IProductSettings {
  enableRulers: boolean;
  visibleRegion: boolean;
  enableViolationWarning: boolean;
  enableQualityChangeContainers: boolean;
  enableGrid: boolean;
  gridStep: number;
  unit: "in" | "mm" | "pt" | "px";
  defaultFont: string;
  colorSpace: TColorSpace;
  colorProfileCmykId: string;
  colorProfileRgbId: string;
  enableSnapToGrid: boolean;
  enableSnapToItems: boolean;
  enableSnapToLocked: boolean;
  enableSnapToPage: boolean;
  enableSnapToRegion: boolean;
  enableSnapToSafetyLines: boolean;
  bulletChar: string;
  firstLineIndent: number;
  levelIndent: number;
  listIndent: number;
  maxLevel: number;
  tabOffset: number;
  numberingFormat: number;
}

export interface IPrintProductModel {
  id: string;
  name: string;
  version: string;
  surfaces: ISurface[];
  tags: {
    $$product_settings: IProductSettings;
  };
  defaultSafetyLines: any[];
  defaultCropMarks: any[];
  defaultDesignLocation: {
    isEmpty: boolean;
    x: number;
    y: number;
  };
  watermark: any;
  interactiveZones: any[];
  preferredFonts: any;
  watermarkConfig: any;
}